/* SpawnAnything — disasters as god tools: tornado, meteor shower, black hole, lava flood (simulation side).
   Pure functions over a state object the worker owns (createDisasters) plus the worker's arrays and helpers
   (bindDisasters after every setup). Lockstep-safe: R() + dmath only, fixed iteration order, no allocations per unit.
   Command (feature command, see featCmd in sim.worker.js): { t: 'dz', k: 'tornado' | 'meteore' | 'loch' | 'lava', x, z, dx, dz }
   with x/z = metres * 16 and dx/dz = direction * 127 (0, 0 = pick one at random), all integers. */
import { dsin, dcos, datan2, hyp } from '../dmath.js';
import { isWater } from '../../world/terrain.js';

/* numeric ids of this feature: 34–36 */
export const EV_DISASTER = 34, EV_LAVA = 35, KIND_METEOR = 36;
/** EV_DISASTER = [34, sub, id, x, z, a, b, c]; sub-types: */
export const DZ_TORNADO = 1, DZ_HOLE = 2, DZ_METEOR = 3, DZ_COLLAPSE = 4, DZ_END = 5, DZ_RESYNC = 6; // 6: replay seek, rebuild visuals
/** Lava grid cell in metres. The grid origin is a multiple of it, so floor(x / LAVA_CELL) names a cell on every side. */
export const LAVA_CELL = 2.5;
/** EV_LAVA = [35, cell, x, surface y, z, state (0 none, 1 molten, 2 stone), molten depth, 0] */
export const LAVA_NONE = 0, LAVA_MOLTEN = 1, LAVA_STONE = 2;

/** Fixed parameters; the network only says which disaster and where. */
export const DISASTERS = {
  tornado: { life: 10, speed: 5.5, core: 4.5, reach: 15, lift: 16 },
  meteore: { life: 5, radius: 22, fall: 1.0, aoe: 5.5, dmg: 260, knock: 14 },
  loch: { life: 6.5, reach: 38, horizon: 2.4, height: 7, collapse: 44, dmg: 380, knock: 36 },
  lava: { emit: 4, rate: 6, cool: 9, flow: 0.1 },
};
const MAXD = 12, MAXSRC = 8, MMAX = 256, TAU = 6.283185307179586;
const PULL = 100;               // ground pull (m/s²) at full strength; walking damps it, so it has to be strong
const MINF = 0.1;               // lava thinner than this stops flowing (viscosity)
const DX8 = [1, 1, 0, -1, -1, -1, 0, 1], DZ8 = [0, 1, 1, 1, 0, -1, -1, -1], W8 = [1, 0.7, 1, 0.7, 1, 0.7, 1, 0.7];
const clamp = (v, a, b) => v < a ? a : v > b ? b : v;
const frac = v => v - Math.floor(v);

export function createDisasters() {
  return {
    c: null, list: [], seq: 0, tick: 0,
    mN: 0, MX: new Float64Array(MMAX), MZ: new Float64Array(MMAX), MT: new Float64Array(MMAX), // meteors in flight
    lava: null, src: [],
  };
}
/** Called by the worker after every setup: fresh state + the current arrays / helpers of the sim. */
export function bindDisasters(S, ctx) {
  S.c = ctx; S.list.length = 0; S.seq = 0; S.tick = 0; S.mN = 0; S.lava = null; S.src.length = 0;
}
const bound = c => (c.TR ? Math.min(c.W, c.TH) : c.W) - 4;

/** Validate and start a disaster. Every client drops the same commands (lockstep). */
export function disasterCmd(S, cmd) {
  const c = S.c; if (!c || !c.N) return false;
  const k = cmd.k;
  if (typeof k !== 'string' || !Object.hasOwn(DISASTERS, k)) return false;
  if (!Number.isFinite(cmd.x) || !Number.isFinite(cmd.z)) return false;
  const L = bound(c), x = clamp(cmd.x / 16, -L, L), z = clamp(cmd.z / 16, -L, L);
  if (k === 'lava') {
    if (S.src.length >= MAXSRC) return false;
    const Lv = ensureLava(S, c);
    const i = lavaCell(Lv, x, z);
    S.src.push({ i, t: 0 }); growBox(Lv, i % Lv.n, (i / Lv.n) | 0);
    return true;
  }
  if (S.list.length >= MAXD) return false;
  const o = { k, id: ++S.seq, x, z, t: 0, h: 0, w: 0, next: 0, y: 0 };
  if (k === 'tornado') {
    const dx = Number.isFinite(cmd.dx) ? clamp(cmd.dx, -127, 127) : 0, dz = Number.isFinite(cmd.dz) ? clamp(cmd.dz, -127, 127) : 0;
    o.h = hyp(dx, dz) >= 1 ? datan2(dx, dz) : c.R() * TAU;
  } else if (k === 'meteore') o.h = c.R() * TAU; // all meteors of a shower come in from about the same side
  else o.y = c.gAt(x, z) + DISASTERS.loch.height;
  S.list.push(o);
  return true;
}

export function stepDisasters(S, dt) {
  const c = S.c; if (!c) return;
  S.tick++;
  for (let k = 0; k < S.list.length;) {
    const o = S.list[k];
    const done = o.k === 'tornado' ? stepTornado(S, c, o, dt) : o.k === 'loch' ? stepHole(S, c, o, dt) : stepShower(S, c, o, dt);
    if (done) S.list.splice(k, 1); else k++;
  }
  if (S.mN) stepMeteors(S, c, dt);
  if (S.lava) stepLava(S, c, dt);
}

/* ---- tornado: meanders across the field, pulls units in, lifts them up the funnel and throws them out ---- */
function stepTornado(S, c, o, dt) {
  const D = DISASTERS.tornado;
  o.t += dt;
  if (o.t >= D.life) { c.pushEv(EV_DISASTER, DZ_END, o.id, o.x, o.z, 0, 0, 0); return true; }
  o.w = o.w * 0.97 + (c.R() - 0.5) * 3 * dt; // turn rate drifts, the heading follows: a smooth meander
  o.h += o.w * dt;
  const hx = dsin(o.h), hz = dcos(o.h), L = bound(c);
  let nx = o.x + hx * D.speed * dt, nz = o.z + hz * D.speed * dt;
  if (nx < -L || nx > L || nz < -L || nz > L) { o.h = datan2(-o.x, -o.z); o.w = 0; nx = clamp(nx, -L, L); nz = clamp(nz, -L, L); }
  o.x = nx; o.z = nz;
  const s = Math.min(1, o.t / 0.8, (D.life - o.t) / 1.2);
  const vx = hx * D.speed, vz = hz * D.speed, { X, Y, Z, VX, VY, VZ, AIR, SPIN, QB } = c, ug = c.UG * dt;
  for (let t = 0; t < 2; t++) {
    const B0 = c.army[t]; if (B0.giant && !c.mix) continue; // giants stand in the storm
    const k0 = s / (B0.mass > 1 ? B0.mass : 1), fun = D.core * 1.6;
    const n = c.queryNear(t, o.x, o.z, D.reach);
    for (let q = 0; q < n; q++) {
      const j = QB[q];
      // mixed armies: every unit by its own troop (c.unitOf); classic battles: one value per side as before
      const B = c.mix ? c.unitOf(j) : B0; if (B.giant) continue;
      const k = c.mix ? s / (B.mass > 1 ? B.mass : 1) : k0;
      const ddx = X[j] - o.x, ddz = Z[j] - o.z, d = Math.sqrt(ddx * ddx + ddz * ddz) || 0.01;
      const ux = ddx / d, uz = ddz / d, tx = -uz, tz = ux; // outward, counter-clockwise
      if (AIR[j]) {
        if (d > fun) continue; // thrown units fly on
        if (Y[j] < D.lift * (0.7 + 0.6 * frac(j * 0.6180339887))) {
          VX[j] = vx + (tx * 10 - ux * 3) * k; VZ[j] = vz + (tz * 10 - uz * 3) * k; VY[j] = 6 * k + ug;
        } else { VX[j] = vx + (ux * 16 + tx * 8) * k; VZ[j] = vz + (uz * 16 + tz * 8) * k; VY[j] = 4; }
      } else if (d < D.core && k > 0.3) {
        AIR[j] = 1; Y[j] = Math.max(Y[j], 0.05); VY[j] = 6 * k; SPIN[j] = (c.R() - 0.5) * 16;
        VX[j] = vx + tx * 8 * k; VZ[j] = vz + tz * 8 * k;
      } else {
        const f = (1 - clamp(d / (D.reach + B.r), 0, 1)) * k * PULL * dt;
        VX[j] += (tx * 0.7 - ux) * f; VZ[j] += (tz * 0.7 - uz) * f;
      }
      if (d < fun) c.damage(j, (B.hp * 0.12 + 6) * s * dt, 0, 0, 0);
    }
  }
  if (c.nB && S.tick % 10 === 0) for (let t = 0; t < 2; t++) c.blastB(t, o.x, o.z, D.core * 1.4, 70 * s, 0, 0, -2);
  c.pushEv(EV_DISASTER, DZ_TORNADO, o.id, o.x, o.z, s, D.reach, o.t);
  return false;
}

/* ---- black hole: pulls everything in, units reaching the centre vanish, then it collapses in a shock wave ---- */
function stepHole(S, c, o, dt) {
  const D = DISASTERS.loch;
  o.t += dt;
  if (o.t >= D.life) {
    for (let t = 0; t < 2; t++) c.blast(t, o.x, o.z, D.collapse, D.dmg, D.knock, 0.9, 0, 0, -2);
    c.pushEv(EV_DISASTER, DZ_COLLAPSE, o.id, o.x, o.z, D.collapse, o.y, 0);
    return true;
  }
  const s = Math.min(1, o.t / 1.2), lift = 10 * s;
  const { X, Y, Z, VX, VY, VZ, GY, AIR, SPIN, QB } = c, ug = c.UG * dt;
  for (let t = 0; t < 2; t++) {
    const B0 = c.army[t], k0 = 1 / Math.sqrt(B0.mass > 1 ? B0.mass : 1), eat0 = D.horizon + B0.r;
    const n = c.queryNear(t, o.x, o.z, D.reach);
    for (let q = 0; q < n; q++) {
      const j = QB[q];
      const B = c.mix ? c.unitOf(j) : B0, k = c.mix ? 1 / Math.sqrt(B.mass > 1 ? B.mass : 1) : k0, eat = c.mix ? D.horizon + B.r : eat0;
      const ddx = o.x - X[j], ddz = o.z - Z[j], d = Math.sqrt(ddx * ddx + ddz * ddz) || 0.01;
      const ux = ddx / d, uz = ddz / d, tx = -uz, tz = ux; // inward, swirl
      if (!AIR[j]) {
        if (d < eat * (B.giant ? 1.4 : 1)) { c.damage(j, 1e9, 0, 0, 0); continue; }
        if (d < lift && !B.giant) { AIR[j] = 1; Y[j] = Math.max(Y[j], 0.05); VY[j] = 3; SPIN[j] = (c.R() - 0.5) * 10; continue; }
        const f = s * (1 - clamp(d / (D.reach + B.r), 0, 1)) * k * PULL * 1.2 * dt;
        VX[j] += (ux + tx * 0.4) * f; VZ[j] += (uz + tz * 0.4) * f;
        continue;
      }
      // airborne: spiral in towards the centre (blend the velocity towards an inward + swirling target)
      const ey = o.y - (GY[j] + Y[j]), d3 = Math.sqrt(d * d + ey * ey) || 0.01;
      if (d3 < eat) { c.damage(j, 1e9, 0, 0, 0); continue; }
      const sp = (5 + 70 / (d3 + 2)) * s, b = 0.18;
      const wx = (ddx / d3 + tx * 0.6) * sp, wy = ey / d3 * sp, wz = (ddz / d3 + tz * 0.6) * sp;
      VX[j] += (wx - VX[j]) * b; VY[j] += (wy - VY[j]) * b + ug; VZ[j] += (wz - VZ[j]) * b;
    }
  }
  if (c.nB && S.tick % 10 === 0) for (let t = 0; t < 2; t++) c.blastB(t, o.x, o.z, 14 * s, 90 * s, 0, 0, -2);
  c.pushEv(EV_DISASTER, DZ_HOLE, o.id, o.x, o.z, s, D.reach, o.y);
  return false;
}

/* ---- meteor shower: a few seconds of meteors at random points of the target disc, each one an explosion ---- */
function stepShower(S, c, o, dt) {
  const D = DISASTERS.meteore, L = bound(c);
  o.t += dt;
  while (o.next <= o.t && o.t < D.life) {
    o.next += 0.1 + c.R() * 0.08;
    const a = c.R() * TAU, r = D.radius * Math.sqrt(c.R());
    const x = clamp(o.x + dcos(a) * r, -L, L), z = clamp(o.z + dsin(a) * r, -L, L);
    const fall = D.fall + c.R() * 0.3, h = o.h + (c.R() - 0.5) * 0.4;
    if (S.mN >= MMAX) continue;
    S.MX[S.mN] = x; S.MZ[S.mN] = z; S.MT[S.mN] = fall; S.mN++;
    c.pushEv(EV_DISASTER, DZ_METEOR, o.id, x, z, fall, h, 0);
  }
  return o.t >= D.life;
}
function stepMeteors(S, c, dt) {
  const D = DISASTERS.meteore;
  for (let k = 0; k < S.mN;) {
    S.MT[k] -= dt;
    if (S.MT[k] > 0) { k++; continue; }
    const x = S.MX[k], z = S.MZ[k];
    for (let t = 0; t < 2; t++) c.blast(t, x, z, D.aoe, D.dmg, D.knock, 1.0, 0, 0, -2);
    c.pushEv(c.EV_EXPL, x, c.gAt(x, z) + 0.3, z, D.aoe, KIND_METEOR, -1, 0);
    S.mN--; S.MX[k] = S.MX[S.mN]; S.MZ[k] = S.MZ[S.mN]; S.MT[k] = S.MT[S.mN];
  }
}

/* ---- lava flood: cellular flow on a coarse grid, downhill along the terrain; cools to stone ----
   V = molten depth, SN = stone depth (solidified lava raises the surface), G = ground height, T = time since last inflow. */
function ensureLava(S, c) {
  if (S.lava) return S.lava;
  let n = Math.ceil(2 * (bound(c) + 4) / LAVA_CELL); n += n & 1; // even: the origin is a multiple of LAVA_CELL
  const N = n * n, o = -(n / 2) * LAVA_CELL;
  const G = new Float32Array(N), WTR = new Uint8Array(N);
  for (let i = 0; i < N; i++) {
    const x = o + ((i % n) + 0.5) * LAVA_CELL, z = o + (((i / n) | 0) + 0.5) * LAVA_CELL;
    G[i] = c.gAt(x, z);
    if (c.TR && isWater(c.TR, x, z)) WTR[i] = 1;
  }
  S.lava = {
    n, o, G, WTR, V: new Float32Array(N), SN: new Float32Array(N), T: new Float32Array(N), F: new Float32Array(N),
    Q: new Int16Array(N), ST: new Uint8Array(N), x0: n, z0: n, x1: -1, z1: -1, molten: 0,
  };
  return S.lava;
}
function lavaCell(Lv, x, z) {
  const n = Lv.n, gx = clamp(Math.floor((x - Lv.o) / LAVA_CELL), 0, n - 1), gz = clamp(Math.floor((z - Lv.o) / LAVA_CELL), 0, n - 1);
  return gz * n + gx;
}
function growBox(Lv, x, z) {
  if (x < Lv.x0) Lv.x0 = x; if (x > Lv.x1) Lv.x1 = x;
  if (z < Lv.z0) Lv.z0 = z; if (z > Lv.z1) Lv.z1 = z;
}
function stepLava(S, c, dt) {
  const Lv = S.lava, D = DISASTERS.lava, n = Lv.n, { G, WTR, V, SN, T, F } = Lv;
  for (let k = 0; k < S.src.length;) {
    const s = S.src[k];
    s.t += dt;
    if (WTR[s.i]) SN[s.i] += D.rate * dt; else { V[s.i] += D.rate * dt; T[s.i] = 0; }
    if (s.t >= D.emit) S.src.splice(k, 1); else k++;
  }
  if (Lv.x1 < 0) return;
  const tick = S.tick;
  // flow every second tick: outflow to lower neighbours (8-way, diagonals weaker), buffered in F so the order does not matter
  if ((tick & 1) === 0) {
    const ex0 = Math.max(0, Lv.x0 - 1), ex1 = Math.min(n - 1, Lv.x1 + 1), ez0 = Math.max(0, Lv.z0 - 1), ez1 = Math.min(n - 1, Lv.z1 + 1);
    for (let z = Lv.z0; z <= Lv.z1; z++) {
      for (let x = Lv.x0; x <= Lv.x1; x++) {
        const i = z * n + x, v = V[i];
        if (v <= MINF) continue;
        const h = G[i] + SN[i] + v;
        let sum = 0;
        for (let d = 0; d < 8; d++) {
          const ax = x + DX8[d], az = z + DZ8[d]; if (ax < 0 || az < 0 || ax >= n || az >= n) continue;
          const j = az * n + ax, dh = h - (G[j] + SN[j] + V[j]);
          if (dh > 0) sum += dh * W8[d];
        }
        if (sum <= 0) continue;
        const mv = Math.min(v - MINF, sum * 0.2) / sum;
        for (let d = 0; d < 8; d++) {
          const ax = x + DX8[d], az = z + DZ8[d]; if (ax < 0 || az < 0 || ax >= n || az >= n) continue;
          const j = az * n + ax, dh = h - (G[j] + SN[j] + V[j]);
          if (dh > 0) { const f = mv * dh * W8[d]; F[j] += f; F[i] -= f; }
        }
      }
    }
    for (let z = ez0; z <= ez1; z++) {
      for (let x = ex0; x <= ex1; x++) {
        const i = z * n + x, f = F[i];
        if (f === 0) continue;
        F[i] = 0;
        if (WTR[i]) { if (f > 0) SN[i] += f; continue; } // lava running into water turns to stone at once
        V[i] += f; if (V[i] < 0) V[i] = 0;
        if (f > 0.003) T[i] = 0;
        if (f > 0) growBox(Lv, x, z);
      }
    }
  }
  // cooling: lava that got no fresh inflow for a while solidifies
  let molten = 0;
  for (let z = Lv.z0; z <= Lv.z1; z++) {
    for (let x = Lv.x0; x <= Lv.x1; x++) {
      const i = z * n + x;
      if (V[i] <= 0) continue;
      T[i] += dt;
      if (T[i] > D.cool + frac(i * 0.6180339887) * 4) { SN[i] += V[i]; V[i] = 0; } else molten++;
    }
  }
  Lv.molten = molten;
  if (molten && (tick & 1) === 1) burnUnits(Lv, c, dt * 2);
  if (c.nB && tick % 10 === 0 && molten) burnBuildings(Lv, c, dt * 10);
  if (tick % 3 === 0) lavaEvents(Lv, c);
  if (!molten && !S.src.length) { lavaEvents(Lv, c); Lv.x0 = Lv.z0 = n; Lv.x1 = Lv.z1 = -1; } // all solid: stop simulating
}
const hot = (Lv, x, z) => Lv.V[lavaCell(Lv, x, z)] > 0.02;
function burnUnits(Lv, c, dt) {
  const L = LAVA_CELL, cx = Lv.o + (Lv.x0 + Lv.x1 + 1) * L / 2, cz = Lv.o + (Lv.z0 + Lv.z1 + 1) * L / 2;
  const r = hyp((Lv.x1 - Lv.x0 + 1) * L, (Lv.z1 - Lv.z0 + 1) * L) / 2 + L, { X, Z, AIR, QB } = c;
  for (let t = 0; t < 2; t++) {
    const B = c.army[t], dmg = (B.hp * (B.giant ? 0.2 : 0.45) + 25) * dt;
    const n = c.queryNear(t, cx, cz, r);
    for (let q = 0; q < n; q++) {
      const j = QB[q]; if (AIR[j] || !hot(Lv, X[j], Z[j])) continue;
      const U = c.mix ? c.unitOf(j) : null; // mixed armies: the burn follows the unit's own troop
      c.damage(j, U ? (U.hp * (U.giant ? 0.2 : 0.45) + 25) * dt : dmg, 0, 0, 0);
    }
  }
}
function burnBuildings(Lv, c, dt) {
  for (let b = 0; b < c.nB; b++) {
    if (!c.BALIVE[b]) continue;
    const B = c.BLD[b], hw = (B.hw || 1) * 0.7, hd = (B.hd || 1) * 0.7;
    if (hot(Lv, B.x, B.z) || hot(Lv, B.x - hw, B.z - hd) || hot(Lv, B.x + hw, B.z - hd) || hot(Lv, B.x - hw, B.z + hd) || hot(Lv, B.x + hw, B.z + hd)) c.bDamage(b, 110 * dt);
  }
}
// tell the renderer about cells whose look changed (state, or the surface moved by 10 cm)
function lavaEvents(Lv, c) {
  const n = Lv.n, { G, V, SN, Q, ST } = Lv;
  for (let z = Lv.z0; z <= Lv.z1; z++) {
    for (let x = Lv.x0; x <= Lv.x1; x++) {
      const i = z * n + x, v = V[i], sn = SN[i];
      const st = v > 0.02 ? LAVA_MOLTEN : sn > 0.02 ? LAVA_STONE : LAVA_NONE, q = Math.min(32000, Math.round((v + sn) * 20));
      if (st === ST[i] && (st === LAVA_NONE || Math.abs(q - Q[i]) < 2)) continue;
      ST[i] = st; Q[i] = q;
      c.pushEv(EV_LAVA, i, Lv.o + (x + 0.5) * LAVA_CELL, G[i] + sn + v, Lv.o + (z + 0.5) * LAVA_CELL, st, v, 0);
    }
  }
}
/** Test/debug summary of the lava grid. */
export function lavaStats(S) {
  const Lv = S.lava; if (!Lv) return { molten: 0, stone: 0, cells: 0, volume: 0 };
  let m = 0, s = 0, vol = 0;
  for (let i = 0; i < Lv.V.length; i++) { if (Lv.V[i] > 0.02) m++; else if (Lv.SN[i] > 0.02) s++; vol += Lv.V[i] + Lv.SN[i]; }
  return { molten: m, stone: s, cells: m + s, volume: vol };
}
